import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import { CaesarAvatar } from "./CaesarAvatar";
import { LOGO_URL } from "./BrandLogo";
import AssistantSparkIcon from "./AssistantSparkIcon";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

const SUGGESTIONS_ES = [
    "¿En qué gasté más este mes?",
    "¿Puedo permitirme unas vacaciones de $1,500?",
    "¿Cómo pago mi tarjeta más rápido?",
    "¿Qué suscripción puedo cancelar?",
];
const SUGGESTIONS_EN = [
    "Where did I spend the most this month?",
    "Can I afford a $1,500 vacation?",
    "How can I pay off my credit card faster?",
    "Which subscription should I cancel?",
];

const CaesarWidget = () => {
    const { user, language } = useApp();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const endRef = useRef(null);

    // Load history when opening
    useEffect(() => {
        if (open && user && messages.length === 0) {
            api.get("/ai/chat/history").then((r) => setMessages(r.data)).catch(() => {});
        }
    }, [open, user, messages.length]);

    // Listen for global "caesar:ask" events (from TransactionDrawer, etc.)
    // and "caesar:open" (from mobile bottom nav) to just open the widget.
    useEffect(() => {
        const askHandler = (ev) => {
            const prompt = ev?.detail?.prompt;
            if (!prompt) return;
            setOpen(true);
            setInput(prompt);
        };
        const openHandler = () => setOpen(true);
        window.addEventListener("caesar:ask", askHandler);
        window.addEventListener("caesar:open", openHandler);
        return () => {
            window.removeEventListener("caesar:ask", askHandler);
            window.removeEventListener("caesar:open", openHandler);
        };
    }, []);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    const suggestions = language === "es" ? SUGGESTIONS_ES : SUGGESTIONS_EN;
    // Reshuffle a fresh sample of 3 prompts every conversation turn — kept
    // ABOVE the early return so React Hooks are always called in the same order.
    const suggestionSample = useMemo(() => {
        return [...suggestions].sort(() => Math.random() - 0.5).slice(0, 3);
    }, [suggestions, messages.length, loading]);

    if (!user) return null;

    const send = async (text) => {
        const msg = (text || input).trim();
        if (!msg) return;
        setInput("");
        setMessages((prev) => [...prev, { role: "user", content: msg, created_at: new Date().toISOString() }]);
        setLoading(true);
        try {
            const res = await api.post("/ai/chat", { message: msg });
            setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply, created_at: new Date().toISOString() }]);
        } catch (e) {
            setMessages((prev) => [...prev, { role: "assistant", content: language === "es" ? "Ups, no pude responder. Inténtalo de nuevo." : "Oops, couldn't respond. Try again.", created_at: new Date().toISOString() }]);
        } finally {
            setLoading(false);
        }
    };

    const firstName = user?.name?.split(" ")[0] || "";

    const introText = language === "es"
        ? `Hola${firstName ? ` ${firstName}` : ""}, soy Caesar — tu amigo experto en finanzas personales. Pregúntame lo que quieras sobre tu dinero y te ayudo a mejorar tu salud financiera, paso a paso.`
        : `Hi${firstName ? ` ${firstName}` : ""}, I'm Caesar — your personal finance sidekick. Ask me anything about your money and I'll help you improve your financial health, one smart step at a time.`;

    return (
        <>
            {/* Closed pill button — desktop only; mobile uses central bottom-nav button */}
            <AnimatePresence>
                {!open && (
                    <motion.button
                        initial={{ opacity: 0, y: 20, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.9 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        onClick={() => setOpen(true)}
                        data-testid="caesar-widget-open"
                        className="hidden md:flex fixed bottom-6 right-6 z-50 items-center gap-3 pl-2 pr-6 py-2 rounded-full text-white shadow-2xl hover-lift border border-white/20 overflow-hidden group"
                        aria-label="Talk to Caesar"
                        style={{
                            background: "linear-gradient(120deg, #0AFFB0 0%, #22D3EE 45%, #B36CFF 100%)",
                            boxShadow: "0 12px 32px -6px rgba(10,255,176,0.55), 0 6px 20px -6px rgba(179,108,255,0.55)",
                        }}
                    >
                        <span
                            aria-hidden
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{
                                background: "linear-gradient(120deg, #B36CFF 0%, #FF3EA5 55%, #FF7A45 100%)",
                            }}
                        />
                        <span className="relative grid place-items-center w-10 h-10 rounded-full bg-white text-[#0A0F1F] shadow-inner">
                            <AssistantSparkIcon size={22} />
                        </span>
                        <span className="relative text-[15px] font-bold tracking-tight drop-shadow-sm">
                            {language === "es" ? "Habla con Caesar" : "Talk to Caesar"}
                        </span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Open chat panel */}
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/30 z-40 md:hidden"
                            onClick={() => setOpen(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 60, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 60, scale: 0.98 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="caesar-widget-panel"
                            className="fixed inset-x-0 bottom-0 md:bottom-6 md:right-6 md:inset-x-auto z-50 w-full md:w-[400px] md:max-w-[400px] h-[85vh] md:h-[min(78vh,640px)] rounded-t-3xl md:rounded-3xl bg-[hsl(var(--card))] shadow-2xl border border-[hsl(var(--border))] overflow-hidden flex flex-col"
                        >
                            {/* Header — premium gradient with neon status pulse */}
                            <div
                                className="flex items-center gap-3 px-5 py-4 relative overflow-hidden text-white"
                                style={{
                                    background: "linear-gradient(120deg, #0A0F1F 0%, #14173A 45%, #2A1454 100%)",
                                }}
                            >
                                <div
                                    className="absolute inset-0 pointer-events-none opacity-70"
                                    style={{
                                        background:
                                            "radial-gradient(circle at 15% 30%, rgba(10,255,176,0.35) 0%, transparent 55%), radial-gradient(circle at 90% 80%, rgba(179,108,255,0.35) 0%, transparent 55%)",
                                    }}
                                />
                                <div className="relative w-12 h-12 rounded-full bg-white/10 grid place-items-center overflow-hidden ring-2 ring-[#0AFFB0]/60">
                                    <img src={LOGO_URL} alt="Vive a lo Grande" className="w-11 h-11 object-contain" />
                                </div>
                                <div className="relative flex-1 min-w-0">
                                    <div className="font-display font-bold text-lg leading-tight text-white">Caesar</div>
                                    <div className="text-xs text-white/85 flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#0AFFB0] animate-pulse" />
                                        {language === "es" ? "En línea" : "Online"}
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    data-testid="caesar-widget-close"
                                    className="relative p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                                    aria-label="Close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3.5" data-testid="caesar-widget-messages">
                                {/* Intro bubble */}
                                <div className="flex items-start gap-2">
                                    <div className="w-9 h-9 rounded-full bg-[hsl(var(--secondary))] grid place-items-center shrink-0 overflow-hidden">
                                        <img src={LOGO_URL} alt="Vive a lo Grande" className="w-8 h-8 object-contain" />
                                    </div>
                                    <div className="flex-1 px-4 py-3 rounded-2xl rounded-bl-sm bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] text-[15px] leading-relaxed font-medium border border-[hsl(var(--border))]">
                                        {introText}
                                    </div>
                                </div>

                                {/* Real messages */}
                                {messages.map((m, i) => (
                                    <div key={i} className={`flex items-start gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                                        {m.role === "assistant" && (
                                            <div className="w-9 h-9 rounded-full bg-[hsl(var(--secondary))] grid place-items-center shrink-0 overflow-hidden ring-1 ring-[hsl(var(--primary))]/25">
                                                <img src={LOGO_URL} alt="Vive a lo Grande" className="w-8 h-8 object-contain" />
                                            </div>
                                        )}
                                        <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap font-medium ${
                                            m.role === "user"
                                                ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] rounded-br-sm shadow-md"
                                                : "bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] rounded-bl-sm border border-[hsl(var(--primary))]/15"
                                        }`}>
                                            {m.content}
                                        </div>
                                    </div>
                                ))}

                                {loading && (
                                    <div className="flex items-center gap-2">
                                        <div className="w-9 h-9 rounded-full bg-[hsl(var(--secondary))] grid place-items-center shrink-0 overflow-hidden ring-1 ring-[hsl(var(--primary))]/25">
                                            <img src={LOGO_URL} alt="Vive a lo Grande" className="w-8 h-8 object-contain" />
                                        </div>
                                        <div className="px-4 py-3 rounded-2xl bg-[hsl(var(--secondary))] flex items-center gap-2 border border-[hsl(var(--primary))]/15">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            <span className="text-sm">{language === "es" ? "Pensando…" : "Thinking…"}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Preset questions — 3 randomized prompts shown once, after each response */}
                                {!loading && (
                                    <div className="pt-1">
                                        <div className="text-[10px] font-bold tracking-[0.24em] text-[hsl(var(--muted-foreground))] uppercase text-center mb-3 flex items-center gap-2 justify-center">
                                            <span className="h-px w-8 bg-[hsl(var(--border))]" />
                                            <span>{language === "es" ? "PRUEBA" : "TRY"}</span>
                                            <span className="h-px w-8 bg-[hsl(var(--border))]" />
                                        </div>
                                        <div className="flex flex-wrap gap-2 justify-center">
                                            {suggestionSample.map((s, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => send(s)}
                                                    data-testid={`caesar-widget-suggestion-${i}`}
                                                    className="pill text-[13px] font-medium px-3.5 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--primary))]/25 text-[hsl(var(--foreground))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-all text-left"
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div ref={endRef} />
                            </div>

                            {/* Input */}
                            <div className="p-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))] flex gap-2">
                                <input
                                    type="text"
                                    placeholder={language === "es" ? "Escribe tu pregunta…" : "Type your question…"}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                                    data-testid="caesar-widget-input"
                                    className="flex-1 pill h-11 px-4 text-[15px] bg-[hsl(var(--secondary))]/50 border border-transparent focus:border-[hsl(var(--primary))] outline-none"
                                />
                                <button
                                    onClick={() => send()}
                                    disabled={loading || !input.trim()}
                                    data-testid="caesar-widget-send"
                                    className="w-11 h-11 rounded-full bg-[hsl(var(--foreground))] text-[hsl(var(--background))] grid place-items-center disabled:opacity-50 hover:opacity-90 transition-opacity"
                                    aria-label="Send"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default CaesarWidget;
